import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AppSettingsProvider } from "@/context/AppSettings";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/booking/AppShell";
import Landing from "@/pages/Landing";
import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";
import Dashboard from "@/pages/booking/Dashboard";
import Intelligence from "@/pages/booking/Intelligence";
import BusinessMemory from "@/pages/booking/BusinessMemory";
const HomeDash = () => { const { isAdmin } = useAuth(); return isAdmin ? <Intelligence /> : <Dashboard />; };
import CalendarPage from "@/pages/booking/CalendarPage";
import Sales from "@/pages/booking/Sales";
import Services from "@/pages/booking/Services";
import Staff from "@/pages/booking/Staff";
import Clients from "@/pages/booking/Clients";
import Settings from "@/pages/booking/Settings";
import Inventory from "@/pages/booking/Inventory";
import Packages from "@/pages/booking/Packages";
import BusinessInfo from "@/pages/booking/BusinessInfo";
import PublicBooking from "@/pages/public/PublicBooking";
import MyBookings from "@/pages/public/MyBookings";
import WaitlistOffer from "@/pages/public/WaitlistOffer";
import PortalInvite from "@/pages/public/PortalInvite";
import PaymentSuccess from "@/pages/booking/PaymentSuccess";
import PaymentCancel from "@/pages/booking/PaymentCancel";
import Calculator from "@/pages/Calculator";
import TreatmentBuilder from "@/pages/TreatmentBuilder";
import PriceList from "@/pages/PriceList";
import BusinessSetup from "@/pages/BusinessSetup";
import Reports from "@/pages/booking/Reports";
import Finance from "@/pages/booking/Finance";
import Forms from "@/pages/booking/Forms";
import ImportData from "@/pages/booking/ImportData";
import ChartTemplates from "@/pages/booking/ChartTemplates";
import Messages from "@/pages/booking/Messages";
import Waitlist from "@/pages/booking/Waitlist";
import MetaAds from "@/pages/booking/MetaAds";
import Integrations from "@/pages/booking/Integrations";
import PlatformLogin from "@/pages/platform/PlatformLogin";
import PlatformDashboard from "@/pages/platform/PlatformDashboard";
import Billing from "@/pages/booking/Billing";
import ConsultationForm from "@/pages/public/ConsultationForm";
import ShortLink from "@/pages/public/ShortLink";
import PublicSurvey from "@/pages/public/PublicSurvey";
import CRM from "@/pages/booking/CRM";
import Rewards from "@/pages/booking/Rewards";
import AftercareTemplates from "@/pages/booking/AftercareTemplates";
import Privacy from "@/pages/Privacy";
import DataDeletion from "@/pages/DataDeletion";
import MetaReportPublic from "@/pages/public/MetaReportPublic";
import Support from "@/pages/Support";
import Contact from "@/pages/Contact";
import Terms from "@/pages/Terms";
import HelpCenter from "@/pages/help/HelpCenter";
import HelpArticle from "@/pages/help/HelpArticle";
import Aftercare from "@/pages/public/Aftercare";
import Instructions from "@/pages/public/Instructions";

const RoleGuard = ({ allow, children }) => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  const rk = user.role === "owner" || user.role === "admin" ? "admin" : user.role;
  if (!allow.includes(rk)) return <Navigate to="/calendar" replace />;
  return children;
};
const Admin = ({ children }) => <RoleGuard allow={["admin"]}>{children}</RoleGuard>;
const PermGuard = ({ perm, children }) => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  const isAdmin = user.role === "owner" || user.role === "admin";
  const ok = isAdmin || !!(user.effective_permissions || {})[perm];
  if (!ok) return <Navigate to="/calendar" replace />;
  return children;
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <AppSettingsProvider>
          <BrowserRouter>
            <TooltipProvider>
            <Toaster position="bottom-right" richColors />
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/features" element={<Landing />} />
              <Route path="/marketing" element={<Landing />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/book/:slug" element={<PublicBooking />} />
              <Route path="/platform/login" element={<PlatformLogin />} />
              <Route path="/platform" element={<PlatformDashboard />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/data-deletion" element={<DataDeletion />} />
              <Route path="/r/meta/:token" element={<MetaReportPublic />} />
              <Route path="/support" element={<Support />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/consult/:token" element={<ConsultationForm />} />
              <Route path="/s/:code" element={<ShortLink />} />
              <Route path="/my/:token" element={<MyBookings />} />
              <Route path="/offer/:token" element={<WaitlistOffer />} />
              <Route path="/portal/:token" element={<PortalInvite />} />
              <Route path="/survey/:token" element={<PublicSurvey />} />
              <Route path="/aftercare/:token" element={<Aftercare />} />
              <Route path="/i/:token" element={<Instructions />} />
              <Route path="/help" element={<HelpCenter />} />
              <Route path="/help/:slug" element={<HelpArticle />} />
              <Route element={<AppShell />}>
                <Route path="/dashboard" element={<HomeDash />} />
                <Route path="/intelligence" element={<Admin><Intelligence /></Admin>} />
                <Route path="/dashboard/classic" element={<Navigate to="/dashboard" replace />} />
                <Route path="/coach" element={<Navigate to="/dashboard" replace />} />
                <Route path="/intelligence/memory" element={<Admin><BusinessMemory /></Admin>} />
                <Route path="/intelligence/opportunities" element={<Navigate to="/intelligence" replace />} />
                <Route path="/content" element={<Navigate to="/intelligence" replace />} />
                <Route path="/calendar" element={<CalendarPage />} />
                <Route path="/sales" element={<RoleGuard allow={["admin", "staff", "reception"]}><Sales /></RoleGuard>} />
                <Route path="/payment/success" element={<PaymentSuccess />} />
                <Route path="/payment/cancel" element={<PaymentCancel />} />
                <Route path="/services" element={<Admin><Services /></Admin>} />
                <Route path="/staff" element={<Admin><Staff /></Admin>} />
                <Route path="/clients" element={<RoleGuard allow={["admin", "reception", "staff"]}><Clients /></RoleGuard>} />
                <Route path="/settings" element={<Admin><Settings /></Admin>} />
                <Route path="/inventory" element={<Admin><Inventory /></Admin>} />
                <Route path="/packages" element={<Admin><Packages /></Admin>} />
                <Route path="/overheads" element={<Navigate to="/finance/bills" replace />} />
                <Route path="/business-info" element={<Admin><BusinessInfo /></Admin>} />
                <Route path="/reports" element={<PermGuard perm="view_reports"><Reports /></PermGuard>} />
                <Route path="/finance/*" element={<Finance />} />
                <Route path="/forms" element={<RoleGuard allow={["admin", "reception"]}><Forms /></RoleGuard>} />
                <Route path="/import" element={<Admin><ImportData /></Admin>} />
                <Route path="/chart-templates" element={<Admin><ChartTemplates /></Admin>} />
                <Route path="/messages" element={<Admin><Messages /></Admin>} />
                <Route path="/waitlist" element={<RoleGuard allow={["admin", "reception"]}><Waitlist /></RoleGuard>} />
                <Route path="/integrations" element={<Admin><Integrations /></Admin>} />
                <Route path="/meta-ads" element={<Admin><MetaAds /></Admin>} />
                <Route path="/expenses" element={<Navigate to="/finance/expenses" replace />} />
                <Route path="/surveys" element={<Navigate to="/settings" replace />} />
                <Route path="/crm" element={<Admin><CRM /></Admin>} />
                <Route path="/rewards" element={<Admin><Rewards /></Admin>} />
                <Route path="/assistant" element={<Navigate to="/intelligence" replace />} />
                <Route path="/aftercare-templates" element={<Admin><AftercareTemplates /></Admin>} />
                <Route path="/automations" element={<Navigate to="/messages" replace />} />
                <Route path="/billing" element={<Admin><Billing /></Admin>} />
                <Route path="/tools/calculator" element={<Admin><Calculator /></Admin>} />
                <Route path="/treatments" element={<Admin><TreatmentBuilder /></Admin>} />
                <Route path="/price-list" element={<RoleGuard allow={["admin", "staff", "reception"]}><PriceList /></RoleGuard>} />
                <Route path="/business-setup" element={<Admin><BusinessSetup /></Admin>} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </TooltipProvider>
          </BrowserRouter>
        </AppSettingsProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
